import { RobotApi } from 'api/RobotApi'
import { ApiError } from 'api/schema'
import { catalogCrossTabLog } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogCrossTabDebug'
import { clearCatalogOperationTimeoutNotice } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogOperationTimeoutNotice'
import { writeCrossTabCatalogModeBusy } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogServiceCrossTab'
import { RobotActions } from 'store/actions/robot'
import { PROGRESS_TYPES, ROBOT_MODES } from 'utils/constants'
import { RootState } from 'utils/interfaces'

export type RobotModeSwitchOptions = {
  actionKey: string
  awaitCalib?: import('utils/interfaces').ServiceCalibAwaitOutcome
  /** FMS lot type / maintenance point → ROS action_type for status icons (modes 15–17). */
  catalogPointActionType?: number | null
}

const setRobotMode = (
  robotId: number,
  mode: ROBOT_MODES,
  metadata?: import('api/RobotApi').RobotModeMetadata,
  options?: RobotModeSwitchOptions
) => {
  return async (dispatch: any, getState: () => RootState) => {
    if (options?.actionKey) {
      clearCatalogOperationTimeoutNotice()
      writeCrossTabCatalogModeBusy(robotId, {
        actionKey: options.actionKey,
        mode,
        awaitCalib: options.awaitCalib,
      })
      dispatch(
        RobotActions.setServiceModeSwitchOptimistic({
          mode,
          actionKey: options.actionKey,
          awaitCalib: options.awaitCalib,
        })
      )
    }
    try {
      await RobotApi.switchMode(robotId, mode, metadata)
      await dispatch(robotThunks.fetchRobotSilent(robotId))
    } catch (error) {
      writeCrossTabCatalogModeBusy(robotId, null)
      dispatch(RobotActions.clearServiceModeSwitchOptimistic())
      dispatch(RobotActions.setUploadingJobProgress(PROGRESS_TYPES.ERROR))
      console.log('Error setting robot mode', error)
      throw error
    }
  }
}

const setRobotNotSetMode = (robotId: number, options?: RobotModeSwitchOptions) => {
  return async (dispatch: any, getState: () => RootState) => {
    const stopItemKey = options?.actionKey?.startsWith('catalog-service-stop:')
      ? options.actionKey.slice('catalog-service-stop:'.length)
      : null
    if (options?.actionKey) {
      clearCatalogOperationTimeoutNotice()
      writeCrossTabCatalogModeBusy(robotId, {
        actionKey: options.actionKey,
        mode: ROBOT_MODES.MODE_NOT_SET,
        awaitCalib: options.awaitCalib,
      })
      catalogCrossTabLog('stop-dispatch', { robotId, actionKey: options.actionKey, stopItemKey })
      dispatch(
        RobotActions.setServiceModeSwitchOptimistic({
          mode: ROBOT_MODES.MODE_NOT_SET,
          actionKey: options.actionKey,
          awaitCalib: options.awaitCalib,
        })
      )
    }
    try {
      await RobotApi.switchMode(robotId, ROBOT_MODES.MODE_NOT_SET)
      await dispatch(robotThunks.fetchRobotSilent(robotId))
    } catch (error) {
      writeCrossTabCatalogModeBusy(robotId, null)
      dispatch(RobotActions.clearServiceModeSwitchOptimistic())
      dispatch(RobotActions.setUploadingJobProgress(PROGRESS_TYPES.ERROR))
      console.log('Error setting robot mode')
      throw error
    }
  }
}

const fetchRobot = (robotId: number) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      dispatch(RobotActions.setLoadingProgress(PROGRESS_TYPES.WORK))
      const robotInfo = await RobotApi.fetchRobot(robotId)
      dispatch(RobotActions.setRobotInfo(robotInfo))
      dispatch(RobotActions.setLoadingProgress(PROGRESS_TYPES.SUCCESS))
    } catch (error) {
      // Silently handle network errors (ERR_NETWORK_CHANGED, Failed to fetch, etc.)
      // These are common when network connection is unstable and don't need user attention
      if (error instanceof TypeError && (error.message === 'Failed to fetch' || error.message.includes('network'))) {
        // Network error - silently ignore, will retry on next interval
        // Don't set ERROR state for network issues
        return
      }
      // For other errors, set error state
      dispatch(RobotActions.setLoadingProgress(PROGRESS_TYPES.ERROR))
      console.warn('[robotThunks] fetchRobot error:', error)
      if (error instanceof ApiError && error.status === 404) {
        throw error
      }
    }
  }
}

/** Fetch robot without changing loadingProgress (for polling on Mission Control so interval is not affected). */
const fetchRobotSilent = (robotId: number) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const robotInfo = await RobotApi.fetchRobot(robotId)
      dispatch(RobotActions.setRobotInfo(robotInfo))
    } catch (error) {
      if (error instanceof TypeError && (error.message === 'Failed to fetch' || error.message.includes('network'))) {
        return
      }
      console.warn('[robotThunks] fetchRobotSilent error:', error)
    }
  }
}

const ensureLidarMapLayer = (robotId: number, mapName: string, facilityId?: number | null) => {
  return async (dispatch: any, getState: () => RootState) => {
    const state = getState()
    const currentLayer = state.robot.lidarMapLayer.layer
    const currentLayerMatches =
      currentLayer?.name === mapName && currentLayer?.robotId === robotId && currentLayer?.facilityId === facilityId
    const isCurrentlyWorkingOnThisMap =
      state.robot.lidarMapLayer.loadingMapName === mapName &&
      state.robot.lidarMapLayer.loadingRobotId === robotId &&
      state.robot.lidarMapLayer.loadingFacilityId === facilityId &&
      (state.robot.lidarMapLayer.loadingProgress === PROGRESS_TYPES.WORK ||
        state.robot.lidarMapLayer.loadingProgress === PROGRESS_TYPES.ERROR)

    if (isCurrentlyWorkingOnThisMap && currentLayerMatches) {
      return
    }

    const scheduleRetry = () => {
      setTimeout(() => {
        const currentState = getState()
        if (
          currentState.robot.lidarMapLayer.loadingMapName === mapName &&
          currentState.robot.lidarMapLayer.loadingRobotId === robotId &&
          currentState.robot.lidarMapLayer.loadingFacilityId === facilityId &&
          currentState.robot.lidarMapLayer.loadingProgress === PROGRESS_TYPES.ERROR
        ) {
          // Resetting to IDLE triggers RobotView's useEffect to automatically retry the fetch
          dispatch(RobotActions.setLidarMapLoadingState({ progress: PROGRESS_TYPES.IDLE, name: undefined }))
        }
      }, 5000)
    }

    try {
      dispatch(
        RobotActions.setLidarMapLoadingState({ progress: PROGRESS_TYPES.WORK, name: mapName, robotId, facilityId })
      )

      // Only clear if the map actually changed, to avoid flickering and infinite loops in useEffect
      if (!currentLayerMatches) {
        dispatch(RobotActions.setLidarMapLayer(null))
      }

      // 1. Find if layer already exists
      const layers = await RobotApi.fetchRobotMapLayers(robotId, mapName)
      let layer = layers.results[0]

      // 2. Create if not exists
      if (!layer) {
        layer = await RobotApi.createRobotMapLayer({
          robot_id: robotId,
          name: mapName,
        })
        // create endpoint returns write serializer (no status field), so always link after creation
        layer = await RobotApi.linkRobotMapLayer(layer.id, mapName)
      } else {
        // 3. Relink even when the existing row is OK: RobotMapLayer is unique by (robot, name),
        // while "BaseMap" is reused for every facility. The current robot facility decides which
        // RobotMap should back this layer.
        layer = await RobotApi.linkRobotMapLayer(layer.id, mapName)
      }

      layer = { ...layer, robotId, facilityId }
      dispatch(RobotActions.setLidarMapLayer(layer))

      // 4. Poll if not ok
      if (layer.status !== 'ok') {
        const interval = setInterval(async () => {
          try {
            // Check if we are still supposed to be loading this map
            const currentState = getState()
            if (
              currentState.robot.lidarMapLayer.loadingMapName !== mapName ||
              currentState.robot.lidarMapLayer.loadingRobotId !== robotId ||
              currentState.robot.lidarMapLayer.loadingFacilityId !== facilityId
            ) {
              clearInterval(interval)
              return
            }

            const updatedLayer = { ...(await RobotApi.fetchRobotMapLayer(layer.id)), robotId, facilityId }

            // Re-check after async call
            const finalState = getState()
            if (
              finalState.robot.lidarMapLayer.loadingMapName !== mapName ||
              finalState.robot.lidarMapLayer.loadingRobotId !== robotId ||
              finalState.robot.lidarMapLayer.loadingFacilityId !== facilityId
            ) {
              clearInterval(interval)
              return
            }

            dispatch(RobotActions.setLidarMapLayer(updatedLayer))
            if (updatedLayer.status === 'ok') {
              clearInterval(interval)
              dispatch(RobotActions.setLidarMapLoadingState({ progress: PROGRESS_TYPES.SUCCESS, name: undefined }))
            } else if (updatedLayer.status === 'error') {
              clearInterval(interval)
              dispatch(
                RobotActions.setLidarMapLoadingState({
                  progress: PROGRESS_TYPES.ERROR,
                  name: mapName,
                  robotId,
                  facilityId,
                })
              )
              dispatch(RobotActions.setLidarMapLayer(null)) // Clear on processing error
              scheduleRetry()
            }
          } catch (pollError) {
            console.error('[robotThunks] ensureLidarMapLayer polling error:', pollError)
            clearInterval(interval)
            dispatch(
              RobotActions.setLidarMapLoadingState({
                progress: PROGRESS_TYPES.ERROR,
                name: mapName,
                robotId,
                facilityId,
              })
            )
            scheduleRetry()
          }
        }, 3000)
      } else {
        dispatch(RobotActions.setLidarMapLoadingState({ progress: PROGRESS_TYPES.SUCCESS, name: undefined }))
      }
    } catch (error) {
      dispatch(
        RobotActions.setLidarMapLoadingState({ progress: PROGRESS_TYPES.ERROR, name: mapName, robotId, facilityId })
      )
      dispatch(RobotActions.setLidarMapLayer(null)) // Clear if linking failed (e.g. 404 from library)
      const status = (error as { status?: number })?.status
      if (status === 404) {
        console.warn(
          `[robotThunks] Lidar map "${mapName}" is not in the facility map library (link-map 404). Auto-retry disabled.`
        )
        return
      }
      console.error('[robotThunks] ensureLidarMapLayer error:', error)
      scheduleRetry()
    }
  }
}

export const robotThunks = {
  setRobotMode,
  fetchRobot,
  fetchRobotSilent,
  setRobotNotSetMode,
  ensureLidarMapLayer,
}
